import { useState } from 'react';
import { toast } from 'react-hot-toast';

import { BasicMenu } from '@client/new/components/BasicMenu/BasicMenu';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { trackEvent } from '@services/tracking';

import {
  downloadAbandonedCartsExport,
  downloadExportAll,
  downloadFreeOffersExport,
  downloadLeadsExport,
  downloadOtpExport,
  downloadSubscribersExport,
} from './CreatorCrm.api';
import { useCrmStore } from './CreatorCrm.store';
import { CrmSegment } from './CrmSegment.enum';

export const ExportButton = () => {
  const [loading, setLoading] = useState(false);
  const crmStore = useCrmStore();
  const influencerId = useInfluencerId();

  const SegmentDownloads: Partial<Record<CrmSegment, () => Promise<void>>> = {
    [CrmSegment.Subscribed]: () =>
      downloadSubscribersExport(influencerId, {
        classification: crmStore.getClassification(),
        status: crmStore.filters.subscription,
        product: crmStore.filters.product,
        searchTerm: crmStore.searchTerm,
        label: crmStore.filters.additional,
      }),
    [CrmSegment.ExclusiveBuyers]: () =>
      downloadOtpExport(influencerId, {
        otpOffers: crmStore.filters.otpOffers,
        searchTerm: crmStore.searchTerm,
        status: crmStore.filters.subscription,
      }),
    [CrmSegment.FreeOffers]: () =>
      downloadFreeOffersExport(influencerId, {
        freeOffers: crmStore.filters.freeOffers,
        convertedTo: crmStore.filters.convertedTo,
        conversionStatus: crmStore.filters.conversionStatus,
        searchTerm: crmStore.searchTerm,
      }),
    [CrmSegment.Leads]: () =>
      downloadLeadsExport(influencerId, {
        searchTerm: crmStore.searchTerm,
        source: crmStore.filters.leadsSource,
      }),
    [CrmSegment.AbandonedCarts]: () =>
      downloadAbandonedCartsExport(influencerId, {
        searchTerm: crmStore.searchTerm,
      }),
  };

  const startDownload = (download: () => Promise<void>) => {
    if (loading) {
      return;
    }
    setLoading(true);

    // `download` builds its filter params synchronously; this turns a throw
    // there into a rejection the chain can catch.
    Promise.resolve()
      .then(download)
      .then(() =>
        toast.success('Started exporting. The download will begin shortly.'),
      )
      .catch((error) =>
        toast.error(
          `Could not export user list. Try again later. Error: ${
            error instanceof Error ? error.message : String(error)
          }`,
        ),
      )
      .finally(() => setLoading(false));
  };

  const handleSegmentExportClick = () => {
    const download = crmStore.segment && SegmentDownloads[crmStore.segment];

    if (!download) {
      toast.error('This table cannot be exported.');
      return;
    }

    trackEvent({
      type: 'Tapped Export This Table',
      payload: {},
    });

    startDownload(download);
  };

  const handleExportClick = () => {
    trackEvent({
      type: 'Tapped Export All Data',
      payload: {},
    });

    startDownload(() => downloadExportAll(influencerId));
  };

  return (
    <div className="mt-5">
      <BasicMenu
        disabled={loading}
        title="Export"
        items={[
          { label: 'Export all data', onClick: handleExportClick },
          { label: 'Export this table', onClick: handleSegmentExportClick },
        ]}
      />
    </div>
  );
};
